import { useState, useEffect, type ReactNode, type KeyboardEvent } from "react";
import { Box, Collapse, Flex, Paper, Text } from "@mantine/core";
import { IconChevronDown } from "@tabler/icons-react";
import {
  PROFILE_SECTION_GAP,
  PROFILE_SECTION_HEADER_PX,
  PROFILE_SECTION_HEADER_PY,
} from "@/shared/styles/profileSectionHeader";

const SECTION_PAPER_STYLE = {
  overflow: "hidden" as const,
  backgroundColor: "#ebf0ff",
  boxShadow: "0 1px 3px rgba(15, 23, 42, 0.08)",
};

export interface ProfileCollapsibleSectionProps {
  title: string;
  icon: ReactNode;
  defaultOpen?: boolean;
  /** Controlled open state; when set, `defaultOpen` is ignored. */
  opened?: boolean;
  onOpenedChange?: (opened: boolean) => void;
  headerRight?: ReactNode;
  mb?: string | number;
  mt?: string | number;
  /** When true, panel content is not rendered while collapsed (single-open accordions). */
  unmountOnCollapse?: boolean;
  /** Collapse open/close duration in ms (default 200). */
  collapseDurationMs?: number;
  children: ReactNode;
}

export default function ProfileCollapsibleSection({
  title,
  icon,
  defaultOpen = false,
  opened: openedProp,
  onOpenedChange,
  headerRight,
  mb = PROFILE_SECTION_GAP,
  mt,
  unmountOnCollapse = false,
  collapseDurationMs = 200,
  children,
}: ProfileCollapsibleSectionProps) {
  const [internalOpened, setInternalOpened] = useState(defaultOpen);
  const isControlled = openedProp !== undefined;
  const opened = isControlled ? openedProp : internalOpened;
  const [renderPanel, setRenderPanel] = useState(opened);

  // Mount panel as soon as `opened` is true; keep mounted until close animation finishes.
  useEffect(() => {
    if (!unmountOnCollapse) return;
    if (opened) {
      setRenderPanel(true);
      return;
    }
    const timer = window.setTimeout(() => setRenderPanel(false), collapseDurationMs);
    return () => window.clearTimeout(timer);
  }, [opened, unmountOnCollapse, collapseDurationMs]);

  const showPanelContent = !unmountOnCollapse || opened || renderPanel;

  const toggle = () => {
    const next = !opened;
    if (isControlled) {
      onOpenedChange?.(next);
    } else {
      setInternalOpened(next);
    }
  };

  const onHeaderKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      toggle();
    }
  };

  return (
    <Paper p={0} radius="lg" mb={mb} mt={mt} style={SECTION_PAPER_STYLE}>
      <Flex
        align="center"
        justify="space-between"
        gap="xs"
        wrap="wrap"
        px={PROFILE_SECTION_HEADER_PX}
        py={PROFILE_SECTION_HEADER_PY}
        style={{ backgroundColor: "#04305c", cursor: "pointer" }}
        onClick={toggle}
        onKeyDown={onHeaderKeyDown}
        role="button"
        tabIndex={0}
        aria-expanded={opened}
      >
        <Flex align="center" gap="xs" style={{ flex: 1, minWidth: 0 }}>
          {icon}
          <Text fw={700} fz="sm" c="#ffc300" style={{ letterSpacing: 0.2, lineHeight: 1.2 }}>
            {title}
          </Text>
        </Flex>
        <Flex align="center" gap="sm" style={{ flexShrink: 0 }}>
          {headerRight ? (
            <Box
              onClick={(event) => event.stopPropagation()}
              onKeyDown={(event) => event.stopPropagation()}
            >
              {headerRight}
            </Box>
          ) : null}
          <Box
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: 24,
              height: 24,
              borderRadius: "50%",
              border: "2.5px solid #ffc300",
              flexShrink: 0,
            }}
          >
            <IconChevronDown
              size={14}
              color="#ffc300"
              stroke={2.5}
              style={{
                transform: opened ? "rotate(180deg)" : "rotate(0deg)",
                transition: `transform ${collapseDurationMs}ms ease-in-out`,
              }}
            />
          </Box>
        </Flex>
      </Flex>
      <Collapse in={opened} transitionDuration={collapseDurationMs} transitionTimingFunction="ease-in-out">
        {showPanelContent ? <Box p={{ base: "lg", sm: "xl" }}>{children}</Box> : null}
      </Collapse>
    </Paper>
  );
}
